import { useCallback } from "react";
import type { Action } from "@core/features/actions";
import { useUpdateActionStatus, ACTION_STATUS } from "@core/features/actions";
import { useUserMeetings } from "@platform/hooks/useUserMeetings";
import { useUserTimezoneOption } from "@platform/hooks/useUserTimezoneOption";
import { AddToCalendarSubStep } from "./AddToCalendarSubStep";

export type Props = {
  /** Program ID */
  programId: number;
  /** The schedule action */
  action: Action | undefined;
  /** The calendar_add action (if sub-step has action_slug) */
  calendarAction: Action | undefined;
  /** Handler for next button */
  onNext: () => void;
  /** Handler for back button (hidden if undefined) */
  onBack?: () => void;
  /** Handler for skip button (hidden if undefined) */
  onSkip?: () => void;
  /** Current step index (0-indexed) */
  stepIndex: number;
  /** Total steps */
  totalSteps: number;
};

/**
 * Container for AddToCalendarSubStep that handles data fetching and mutations.
 */
export function AddToCalendarSubStepContainer({
  programId: _programId,
  action: _action,
  calendarAction,
  onNext,
  onBack,
  onSkip,
  stepIndex,
  totalSteps,
}: Props) {
  const { meetings } = useUserMeetings();
  const userTimezoneOption = useUserTimezoneOption();

  // Hook to mark calendar_add action as completed
  const { mutate: markCalendarComplete } = useUpdateActionStatus({
    actionId: calendarAction?.id ?? 0,
    status: ACTION_STATUS.COMPLETED,
    currentStatus: calendarAction?.current_status?.name,
  });

  // Handler for adding calendar events
  const handleAddCalendarEvent = useCallback(
    (
      _meetingId: number | string | "all",
      _calendarType: "google" | "ical"
    ) => {
      // Mark the calendar_add action as completed if it exists
      if (calendarAction?.id) {
        markCalendarComplete();
      }
    },
    [calendarAction?.id, markCalendarComplete]
  );

  return (
    <AddToCalendarSubStep
      meetings={meetings ?? []}
      userTimezone={userTimezoneOption ?? undefined}
      onAddCalendarEvent={handleAddCalendarEvent}
      onContinue={onNext}
      onBack={onBack}
      onSkip={onSkip}
      stepIndex={stepIndex}
      totalSteps={totalSteps}
    />
  );
}

export default AddToCalendarSubStepContainer;
